import React, { useContext, useState } from 'react';
import {
  CaretDownFilled,
  CaretUpFilled,
  DashOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { Button, Modal, Progress, Tooltip, Typography } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import RemoteAccessDashboardContext from '@totem/components/services/remoteAccess/v2/summary/remoteAccessDashboardContext';
import { RemoteAccessOrganizationRiskSummary } from '@totem/components/services/remoteAccess/v2/summary/types';
import { sortStringAscending } from '@totem/components/services/threats/eventsContainer_legacy/utilities';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import { isNotNull } from '@totem/utilities/common';
import {
  sortNumberAscending,
  sortNumberDescending,
} from '@totem/utilities/tableUtilities';

import './organizationalUsage.css';

const TABLE_TEXT_COLOR = '#c35721';
const TABLE_STRIPE_COLOR = '#ead5ca';

const headerTitle = (label: string) => (
  <span style={{ color: TABLE_TEXT_COLOR, fontWeight: 700 }}>{label}</span>
);

const { Text, Title } = Typography;

// Converts a sub-score (already scaled by its inner weight) back to a 0-100%
// saturation value so it can be displayed as a progress bar.
const toPercent = (value: number, max: number) =>
  Math.min(100, Math.round((value / max) * 100));

const riskColor = (pct: number) => {
  if (pct < 34) return '#52c41a';
  if (pct < 67) return '#faad14';
  return '#ff4d4f';
};

type FactorRowProps = {
  label: string;
  weight: string;
  pct: number;
};

const FactorRow = ({ label, weight, pct }: FactorRowProps) => (
  <div style={{ marginBottom: 6 }}>
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: 2,
      }}
    >
      <Text style={{ fontSize: 13 }}>
        {label}{' '}
        <Text type="secondary" style={{ fontSize: 12 }}>
          ({weight})
        </Text>
      </Text>
      <Text style={{ fontSize: 13, minWidth: 36, textAlign: 'right' }}>
        {pct}%
      </Text>
    </div>
    <Progress
      percent={pct}
      showInfo={false}
      size="small"
      strokeColor={riskColor(pct)}
    />
  </div>
);

type PillarSectionProps = {
  title: string;
  pillarScore: number;
  pillarMax: number;
  children: React.ReactNode;
};

const PillarSection = ({
  title,
  pillarScore,
  pillarMax,
  children,
}: PillarSectionProps) => {
  const pillarPct = toPercent(pillarScore, pillarMax / 10);

  return (
    <div style={{ marginBottom: 20 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 4,
        }}
      >
        <Title level={5} style={{ margin: 0 }}>
          {title}
        </Title>
        <Text style={{ fontSize: 13 }}>
          <Text strong>{pillarScore.toFixed(1)}</Text>
          <Text type="secondary"> / {pillarMax.toFixed(1)}</Text>
        </Text>
      </div>
      <Progress
        percent={pillarPct}
        showInfo={false}
        strokeColor={riskColor(pillarPct)}
        style={{ marginBottom: 10 }}
      />
      <div style={{ paddingLeft: 12 }}>{children}</div>
    </div>
  );
};

const RiskScoreDetailModal = ({
  record,
  onClose,
}: {
  record: RemoteAccessOrganizationRiskSummary | null;
  onClose: () => void;
}) => {
  if (!record) return null;

  const totalDisplay = (record.totalRisk * 10).toFixed(1);
  // Max achievable score is 9.0 (pillars sum to 0.90; 0.10 reserved).
  const totalPct = toPercent(record.totalRisk, 0.9);

  return (
    <Modal
      title={`Risk Breakdown — ${record.vendor}`}
      open
      onCancel={onClose}
      footer={
        <Button type="primary" onClick={onClose}>
          Close
        </Button>
      }
      width={540}
    >
      <div style={{ marginBottom: 24 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginBottom: 4,
          }}
        >
          <Text strong>Total Risk Score</Text>
          <Text>
            <Text strong style={{ fontSize: 20 }}>
              {totalDisplay}
            </Text>
            <Text type="secondary"> / 9.0</Text>
          </Text>
        </div>
        <Progress
          percent={totalPct}
          showInfo={false}
          strokeColor={riskColor(totalPct)}
        />
      </div>

      <PillarSection
        title="Access Risk"
        pillarScore={record.accessRisk * 10}
        pillarMax={3.5}
      >
        <FactorRow
          label="Account inactivity"
          weight="avg. per-user decay, τ = 60 days"
          pct={toPercent(record.neverUsedRisk, 1.0)}
        />
        <FactorRow
          label="User count amplifier"
          weight="log user-count scale"
          pct={toPercent(record.userCountRisk, 1.0)}
        />
      </PillarSection>

      <PillarSection
        title="Control Risk"
        pillarScore={record.controlRisk * 10}
        pillarMax={3.5}
      >
        <FactorRow
          label="Password age"
          weight="avg. per-user decay, τ = 90 days · 70% of pillar"
          pct={toPercent(record.passwordFreshnessRisk, 1.0)}
        />
        <FactorRow
          label="Audit compliance"
          weight="Bayesian-smoothed overdue score · 30% of pillar"
          pct={toPercent(record.auditRisk, 1.0)}
        />
      </PillarSection>

      <PillarSection
        title="Behaviour Risk"
        pillarScore={record.behaviourRisk * 10}
        pillarMax={2.0}
      >
        <FactorRow
          label="After-hours activity"
          weight="proportion of session time outside business hours"
          pct={toPercent(record.afterHoursRisk, 1.0)}
        />
      </PillarSection>
    </Modal>
  );
};

const RiskScoreModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => (
  <Modal
    title="How is the Risk Score calculated?"
    open={open}
    onCancel={onClose}
    footer={
      <Button type="primary" onClick={onClose}>
        Got it
      </Button>
    }
    width={560}
  >
    <Typography>
      <Text>
        The Risk Score (0–10) measures the overall remote access risk for an
        organisation or vendor group. It is built from three pillars, each
        contributing a share of the total score. Risk accumulates continuously
        over time rather than crossing hard thresholds.
      </Text>

      <Title level={5} style={{ marginTop: 20 }}>
        Access Risk &mdash; up to 3.5 / 10
      </Title>
      <Text>How active and well-managed are the accounts?</Text>
      <ul style={{ marginTop: 8 }}>
        <li>
          <Text strong>Account inactivity</Text> &mdash; a per-user exponential
          decay based on days since last login (or account creation for accounts
          that have never logged in). Risk rises sharply in the first 60 days of
          inactivity and approaches maximum beyond that.
        </li>
        <li>
          <Text strong>User count amplifier</Text> &mdash; a log-scaled factor
          that grows with the number of users. Vendors with many accounts carry
          proportionally more access risk even if individual accounts are
          healthy.
        </li>
      </ul>

      <Title level={5} style={{ marginTop: 16 }}>
        Control Risk &mdash; up to 3.5 / 10
      </Title>
      <Text>Are security controls and compliance requirements being met?</Text>
      <ul style={{ marginTop: 8 }}>
        <li>
          <Text strong>Password age (70%)</Text> &mdash; per-user exponential
          decay based on days since last password reset. Risk rises sharply
          beyond 90 days.
        </li>
        <li>
          <Text strong>Audit compliance (30%)</Text> &mdash; continuous overdue
          score per site, based on how far past the configured audit cadence
          each site has drifted. Bayesian smoothing prevents small vendor groups
          from producing extreme scores. Sites that are too new to have
          completed a full audit cycle are excluded.
        </li>
      </ul>

      <Title level={5} style={{ marginTop: 16 }}>
        Behaviour Risk &mdash; up to 2.0 / 10
      </Title>
      <Text>Are access patterns within expected bounds?</Text>
      <ul style={{ marginTop: 8 }}>
        <li>
          <Text strong>After-hours activity</Text> &mdash; the proportion of
          total session time that occurs outside normal business hours.
        </li>
      </ul>

      <Text type="secondary" style={{ display: 'block', marginTop: 16 }}>
        The three pillars combine (35% + 35% + 20%) to produce scores up to 9.0
        out of 10. The remaining 1.0 is reserved for future pillars. A score of{' '}
        <Text strong>0</Text> indicates no detected risk; a score approaching{' '}
        <Text strong>9</Text> indicates maximum risk across all currently
        measured factors.
      </Text>
    </Typography>
  </Modal>
);

const OrganizationalUsage = () => {
  const { organizationSummaryData, organizationSummaryLoading } = useContext(
    RemoteAccessDashboardContext,
  );
  const [riskModalOpen, setRiskModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] =
    useState<RemoteAccessOrganizationRiskSummary | null>(null);

  const columns: ColumnProps<RemoteAccessOrganizationRiskSummary>[] = [
    {
      title: headerTitle('Organization'),
      dataIndex: 'vendor',
      key: 'vendor',
      render: (_, record: RemoteAccessOrganizationRiskSummary) => (
        <span style={{ color: TABLE_TEXT_COLOR }}>{record.vendor}</span>
      ),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) => sortStringAscending(compA.vendor, compB.vendor),
    },
    {
      title: headerTitle('Total Users'),
      dataIndex: 'totalUsers',
      key: 'totalUsers',
      render: (_, record: RemoteAccessOrganizationRiskSummary) => (
        <span style={{ color: TABLE_TEXT_COLOR }}>{record.totalUsers}</span>
      ),
      align: 'center',
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortNumberAscending(compA.totalUsers, compB.totalUsers),
    },
    {
      title: headerTitle('Active'),
      dataIndex: 'activeUsers',
      key: 'activeUsers',
      render: (_, record: RemoteAccessOrganizationRiskSummary) => (
        <span style={{ color: TABLE_TEXT_COLOR }}>{record.activeUsers}</span>
      ),
      align: 'center',
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortNumberAscending(compA.activeUsers, compB.activeUsers),
    },
    {
      title: headerTitle('Stale'),
      dataIndex: 'staleUsers',
      key: 'staleUsers',
      render: (_, record: RemoteAccessOrganizationRiskSummary) => (
        <span style={{ color: TABLE_TEXT_COLOR }}>{record.staleUsers}</span>
      ),
      align: 'center',
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortNumberAscending(compA.staleUsers, compB.staleUsers),
    },
    {
      title: headerTitle('Never Used'),
      dataIndex: 'neverLoggedInUsers',
      key: 'neverLoggedInUsers',
      render: (_, record: RemoteAccessOrganizationRiskSummary) => (
        <span style={{ color: TABLE_TEXT_COLOR }}>
          {record.neverLoggedInUsers}
        </span>
      ),
      align: 'center',
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortNumberAscending(compA.neverLoggedInUsers, compB.neverLoggedInUsers),
    },
    {
      title: headerTitle('Sessions'),
      dataIndex: 'totalSessions',
      key: 'totalSessions',
      render: (_, record: RemoteAccessOrganizationRiskSummary) => (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '12px',
            color: TABLE_TEXT_COLOR,
          }}
        >
          <span style={{ color: TABLE_TEXT_COLOR }}>
            {record.totalSessions}
          </span>
          <span>&nbsp;</span>
          {record.totalSessions > record.previousTotalSessions && (
            <CaretUpFilled
              title={`Previous: ${record.previousTotalSessions}`}
            />
          )}
          {record.totalSessions < record.previousTotalSessions && (
            <CaretDownFilled
              title={`Previous: ${record.previousTotalSessions}`}
            />
          )}
          {record.totalSessions === record.previousTotalSessions && (
            <DashOutlined title={`Previous: ${record.previousTotalSessions}`} />
          )}
        </span>
      ),
      align: 'center',
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortNumberAscending(compA.totalSessions, compB.totalSessions),
    },
    {
      title: headerTitle('Average Duration'),
      dataIndex: 'averageSessionDuration',
      key: 'averageSessionDuration',
      render: (_, record: RemoteAccessOrganizationRiskSummary) => {
        const durationHours = record.averageSessionDuration / 60;
        const roundedDuration = `${durationHours.toFixed(1)} Hours`;
        const previousDurationHours =
          record.previousAverageSessionDuration / 60;
        const previousRoundedDuration = `${previousDurationHours.toFixed(1)} Hours`;

        return (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '12px',
              color: TABLE_TEXT_COLOR,
            }}
          >
            <span style={{ color: TABLE_TEXT_COLOR }}>{roundedDuration}</span>
            <span>&nbsp;</span>
            {record.averageSessionDuration >
              record.previousAverageSessionDuration && (
              <CaretUpFilled title={`Previous: ${previousRoundedDuration}`} />
            )}
            {record.averageSessionDuration <
              record.previousAverageSessionDuration && (
              <CaretDownFilled title={`Previous: ${previousRoundedDuration}`} />
            )}
            {record.averageSessionDuration ===
              record.previousAverageSessionDuration && (
              <DashOutlined title={`Previous: ${previousRoundedDuration}`} />
            )}
          </span>
        );
      },
      align: 'center',
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortNumberAscending(
          compA.averageSessionDuration,
          compB.averageSessionDuration,
        ),
    },
    {
      title: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {headerTitle('Risk Score')}
          <Tooltip title="How is Risk Score calculated?">
            <Button
              type="text"
              size="small"
              icon={<QuestionCircleOutlined />}
              onClick={(evt) => {
                evt.stopPropagation();

                setRiskModalOpen(true);
              }}
              style={{ color: TABLE_TEXT_COLOR, padding: 0, height: 'auto' }}
            />
          </Tooltip>
        </span>
      ),
      dataIndex: 'totalRisk',
      key: 'totalRisk',
      render: (_, record: RemoteAccessOrganizationRiskSummary) => (
        <Tooltip title="View risk breakdown">
          <Button
            type="link"
            style={{ color: TABLE_TEXT_COLOR, padding: 0, fontWeight: 600 }}
            onClick={() => setSelectedRecord(record)}
          >
            {(record.totalRisk * 10).toFixed(1)}
          </Button>
        </Tooltip>
      ),
      align: 'center',
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortNumberAscending(compA.totalRisk, compB.totalRisk),
    },
  ];

  return (
    <>
      <RiskScoreDetailModal
        record={selectedRecord}
        onClose={() => setSelectedRecord(null)}
      />
      <RiskScoreModal
        open={riskModalOpen}
        onClose={() => setRiskModalOpen(false)}
      />
      <IBTable
        showSorterTooltip
        columns={columns}
        dataSource={
          isNotNull(organizationSummaryData)
            ? organizationSummaryData.sort((compA, compB) =>
                sortNumberDescending(compA.totalRisk, compB.totalRisk),
              )
            : []
        }
        loading={organizationSummaryLoading}
        pagination={{
          showSizeChanger: true,
          defaultPageSize: 5,
          pageSizeOptions: [5, 10, 20, 50, 100],
        }}
        onRow={(_, index) => ({
          style: {
            backgroundColor: index && index % 2 === 1 ? TABLE_STRIPE_COLOR : '',
          },
        })}
        rowKey={(record) => record.vendor}
      />
    </>
  );
};

export default OrganizationalUsage;
